// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pill, RefreshCcw, MessageSquare, Loader2, AlertCircle, ShieldAlert } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import FreshnessChip from '../../components/workflow/FreshnessChip'

interface Props { onScreenEnter: () => void }

/**
 * Patient meds dashboard (Sprint 10 / WI-606+607).
 *
 * Lists the patient's prescriptions (read-only mirror from PioneerRx),
 * lets them request a refill, and links to the per-Rx detail where they
 * can post comments to the pharmacy.
 */
export default function PatientDashboard({ onScreenEnter }: Props) {
  const navigate = useNavigate()
  const [items, setItems] = useState<api.PatientRx[]>([])
  const [branches, setBranches] = useState<api.PatientBranch[]>([])
  const [branchFilter, setBranchFilter] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refillBusy, setRefillBusy] = useState<string | null>(null)
  const [refillSuccess, setRefillSuccess] = useState<string | null>(null)

  useEffect(() => {
    onScreenEnter()
    reload(branchFilter)
  }, [onScreenEnter, branchFilter])

  async function reload(branch: string) {
    setLoading(true); setError(null)
    try {
      const r = await api.listPatientRx(branch || undefined)
      setItems(r.items)
      // Always refresh branch options from the server (full set, not the filtered set)
      if (!branch) setBranches(r.branches || [])
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }

  async function requestRefill(rx: api.PatientRx) {
    setRefillBusy(rx.id); setError(null); setRefillSuccess(null)
    try {
      await api.createRefill(rx.id)
      setRefillSuccess(`Refill request submitted for ${rx.drug_name}.`)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setRefillBusy(null)
    }
  }

  const active = items.filter(r => !['cancelled', 'rejected', 'expired'].includes(r.state))
  const inactive = items.filter(r => ['cancelled', 'rejected', 'expired'].includes(r.state))

  return (
    <div style={pageStyle}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={titleStyle}>My medications</h1>
        <p style={subtitleStyle}>
          Your active prescriptions. Tap any item to leave a comment for your pharmacy.
        </p>
      </header>

      {branches.length > 1 && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>Showing:</label>
          <select
            value={branchFilter}
            onChange={e => setBranchFilter(e.target.value)}
            style={{
              padding: '6px 10px', fontSize: 13,
              border: '1px solid var(--color-border)', borderRadius: 4,
              background: 'var(--color-surface)', fontFamily: 'inherit',
            }}
          >
            <option value="">All branches ({branches.length})</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>
                {b.branch_label ? `${b.branch_label} — ${b.name}` : b.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div style={errorBox}><AlertCircle size={16} aria-hidden="true" /> {error}</div>
      )}
      {refillSuccess && (
        <div style={successBox}>{refillSuccess}</div>
      )}

      {loading ? (
        <div style={loadingBox}><Loader2 size={20} className="spin" aria-hidden="true" /> Loading…</div>
      ) : items.length === 0 ? (
        <div style={emptyBox}>
          <Pill size={28} aria-hidden="true" style={{ color: 'var(--color-text-mute)' }} />
          <h2 style={{ fontSize: 16, margin: '8px 0 4px' }}>No prescriptions on file</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-soft)' }}>
            Once your pharmacy fills a prescription for you, it will appear here.
          </p>
        </div>
      ) : (
        <>
          <h2 style={sectionLabel}>Active ({active.length})</h2>
          {active.length === 0 ? (
            <div style={emptyMini}>None active.</div>
          ) : (
            <ul style={listStyle}>
              {active.map(rx => (
                <RxRow
                  key={rx.id}
                  rx={rx}
                  onOpen={() => navigate(`/rx/${rx.id}`)}
                  onRefill={() => requestRefill(rx)}
                  refillBusy={refillBusy === rx.id}
                />
              ))}
            </ul>
          )}

          {inactive.length > 0 && (
            <>
              <h2 style={sectionLabel}>Inactive ({inactive.length})</h2>
              <ul style={listStyle}>
                {inactive.map(rx => (
                  <RxRow
                    key={rx.id}
                    rx={rx}
                    onOpen={() => navigate(`/rx/${rx.id}`)}
                    onRefill={() => {}}
                    refillBusy={false}
                    disableRefill
                  />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}

function RxRow({ rx, onOpen, onRefill, refillBusy, disableRefill }: {
  rx: api.PatientRx
  onOpen: () => void
  onRefill: () => void
  refillBusy: boolean
  disableRefill?: boolean
}) {
  const refillsLeft = Math.max(0, rx.refills_authorized - rx.refills_used)
  const canRefill = !disableRefill && refillsLeft > 0
  return (
    <li style={rowStyle}>
      <button onClick={onOpen} style={rowMainBtnStyle} aria-label={`Open ${rx.drug_name}`}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 15 }}>{rx.drug_name}</strong>
          <span style={{ color: 'var(--color-text-soft)', fontSize: 13 }}>{rx.drug_strength}</span>
          {rx.is_controlled && (
            <span style={schedBadge} title={`DEA Schedule ${rx.schedule ?? 'controlled'}`}>
              <ShieldAlert size={10} aria-hidden="true" /> {rx.schedule ?? 'CTRL'}
            </span>
          )}
          {rx.ship_status === 'delivered' && (
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#dcfce7', color: '#166534' }}>
              ✓ Delivered{rx.ship_delivered_at ? ` ${new Date(rx.ship_delivered_at).toLocaleDateString()}` : ''}
            </span>
          )}
          {rx.ship_status && ['in_transit', 'out_for_delivery', 'label_purchased', 'created', 'picked_up'].includes(rx.ship_status) && (
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#dbeafe', color: '#1e40af' }}>⛟ In transit</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }}>
          {rx.sig}
        </div>
        <div style={metaRowStyle}>
          <span>Prescribed by <strong>{rx.prescriber_name}</strong></span>
          <span>· {rx.quantity} {rx.quantity_unit}</span>
          <span>· {refillsLeft} refill{refillsLeft === 1 ? '' : 's'} left</span>
          <FreshnessChip syncedAt={rx.last_pioneerrx_sync_at} />
        </div>
      </button>
      <div style={rowActionsStyle}>
        <button
          onClick={onRefill}
          disabled={!canRefill || refillBusy}
          style={canRefill ? refillBtn : refillBtnDisabled}
          title={!canRefill ? 'No refills remaining' : 'Request a refill'}
        >
          {refillBusy ? <Loader2 size={13} className="spin" /> : <RefreshCcw size={13} />}
          Refill
        </button>
        <button onClick={onOpen} style={commentBtn} title="Open detail to comment">
          <MessageSquare size={13} aria-hidden="true" />
          {rx.comment_count > 0 && <span>{rx.comment_count}</span>}
        </button>
      </div>
    </li>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: '100%', margin: '0 auto', padding: '24px 24px 48px' }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 6, color: 'var(--color-text-soft)', fontSize: 14 }
const sectionLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
  color: 'var(--color-text-mute)', margin: '20px 0 8px',
}
const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 12, padding: 0, marginBottom: 8,
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden',
}
const rowMainBtnStyle: React.CSSProperties = {
  flex: 1, textAlign: 'left', padding: 14, background: 'transparent',
  border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'inherit',
}
const metaRowStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
  marginTop: 6, fontSize: 11, color: 'var(--color-text-mute)',
}
const rowActionsStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', justifyContent: 'center',
  gap: 6, padding: '14px 14px 14px 0',
}
const refillBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', fontSize: 12, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
}
const refillBtnDisabled: React.CSSProperties = {
  ...refillBtn, background: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed',
}
const commentBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', fontSize: 12, fontWeight: 500,
  background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)', borderRadius: 6,
  cursor: 'pointer', fontFamily: 'inherit',
}
const schedBadge: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3,
  fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
  padding: '1px 6px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
}

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12,
}
const successBox: React.CSSProperties = {
  padding: '10px 14px', background: '#dcfce7', color: '#166534',
  borderRadius: 6, fontSize: 13, marginBottom: 12,
}
const loadingBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 24,
  color: 'var(--color-text-soft)', fontSize: 14,
}
const emptyBox: React.CSSProperties = {
  textAlign: 'center', padding: 32,
  background: 'var(--color-surface, #fff)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
}
const emptyMini: React.CSSProperties = {
  padding: 16, textAlign: 'center', fontSize: 13,
  color: 'var(--color-text-mute)',
  background: 'var(--color-surface, #fff)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
}
